import React, { useEffect, useState } from "react";

import { DynamicForm } from "@shared/components/DynamicForm";

import {
  FormField,
  FormService,
} from "@shared/services/FormServices";

import { Button } from "@shared/ui/button";

import { FIELD_CLASSES } from "@shared/utils/constants";
import { useEntityTypeForParameters } from "@features/onboarding/utility-setup/hooks";

interface ParameterFilterProps {
  currentFilters: {
    entity: string;
    status: string;
  };

  onApplyFilters: (filters: any) => void;

  onResetFilters: () => void;
}

export const ParameterFilter: React.FC<
  ParameterFilterProps
> = ({
  currentFilters,
  onApplyFilters,
  onResetFilters,
}) => {
  const [tempFilters, setTempFilters] =
    useState({
      entity:
        currentFilters.entity || "all",

      status:
        currentFilters.status || "all",
    });

  const { data: entityTypes } = useEntityTypeForParameters();

  // ───────────────────────────────────────────────────────────────────
  // Form Fields
  // ───────────────────────────────────────────────────────────────────

  const createFormFields =
    (): FormField[] => [
      {
        name: "entity",

        type: "select",

        label: "Entity Type",

        placeholder: "Select entity",

        required: false,

        options: [
          {
            label: "All Entities",
            value: "all",
          },
          ...(entityTypes || []).map((type: any) => ({
            label: type.value,
            value: type.key,
          })),
        ],

        classes: FIELD_CLASSES,
      },

      {
        name: "status",

        type: "select",

        label: "Status",

        placeholder: "Select status",

        required: false,

        options: [
          {
            label: "All Statuses",
            value: "all",
          },

          {
            label: "Active",
            value: "1",
          },

          {
            label: "Inactive",
            value: "0",
          },
        ],

        classes: FIELD_CLASSES,
      },
    ];

  // ───────────────────────────────────────────────────────────────────
  // Form
  // ───────────────────────────────────────────────────────────────────

  const form = FormService.createForm({
    fields: createFormFields(),

    defaultValues: tempFilters,
  });

  // ───────────────────────────────────────────────────────────────────
  // Sync Current Filters
  // ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    const updatedFilters = {
      entity:
        currentFilters.entity || "all",

      status:
        currentFilters.status || "all",
    };

    setTempFilters(updatedFilters);

    form.reset(updatedFilters);
  }, [currentFilters]);

  // ───────────────────────────────────────────────────────────────────
  // Submit
  // ───────────────────────────────────────────────────────────────────

  const handleSubmit = (data: any) => {
    onApplyFilters({
      entity:
        data.entity &&
        data.entity !== "all"
          ? data.entity
          : "",

      status:
        data.status &&
        data.status !== "all"
          ? data.status
          : "",
    });
  };

  // ───────────────────────────────────────────────────────────────────
  // Reset
  // ───────────────────────────────────────────────────────────────────

  const handleReset = () => {
    const resetValues = {
      entity: "all",

      status: "all",
    };

    setTempFilters(resetValues);

    form.reset(resetValues);

    onResetFilters();
  };

  // ───────────────────────────────────────────────────────────────────
  // Render
  // ───────────────────────────────────────────────────────────────────

  return (
    <DynamicForm
      fields={createFormFields()}
      form={form}
      onSubmit={handleSubmit}
      onReset={handleReset}
      config={{
        showSubmitButton: false,

        showResetButton: false,

        layout: "vertical",

        spacing: "medium",
      }}
      gridColumns={2}
      responsiveColumns={{
        sm: 1,
        md: 2,
        lg: 2,
      }}
      className="space-y-6"
      renderCustomFooter={() => (
        <div className="col-span-1 md:col-span-2 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
          >
            Reset Filters
          </Button>

          <Button type="submit">
            Apply Filters
          </Button>
        </div>
      )}
    />
  );
};
